/*
 * Omaggi legati al prodotto principale.
 * Le righe omaggio hanno la proprieta' nascosta `_gift_for` = id variante del
 * prodotto principale (impostata in product-template-cart.js / product-form.js).
 * La quantita' dell'omaggio segue quella del prodotto principale (una custodia
 * per ogni unita'); se il principale non e' piu' in carrello, l'omaggio viene rimosso.
 */
const CartGiftLink = {
  property: '_gift_for',

  // Quantita' totale delle righe "principali" (senza proprieta' omaggio) per variante.
  parentQuantities(cart) {
    const totals = {};
    ((cart && cart.items) || []).forEach((item) => {
      if (item.properties && item.properties[this.property]) return;
      const id = String(item.variant_id);
      totals[id] = (totals[id] || 0) + item.quantity;
    });
    return totals;
  },

  // Mappa { line_item_key: quantita' } per le righe omaggio da allineare:
  // stessa quantita' del prodotto principale, 0 se il principale non c'e' piu'.
  pendingUpdates(cart) {
    const parents = this.parentQuantities(cart);
    const updates = {};
    ((cart && cart.items) || []).forEach((item) => {
      const giftFor = item.properties && item.properties[this.property];
      if (!giftFor) return;
      const wanted = parents[String(giftFor)] || 0;
      if (wanted !== item.quantity) updates[item.key] = wanted;
    });
    return updates;
  },

  // Applica gli allineamenti; risolve con lo stato carrello aggiornato
  // (o con quello ricevuto se non c'e' nulla da fare / in caso di errore).
  sync(cart, sections, sectionsUrl) {
    const updates = this.pendingUpdates(cart);
    if (!Object.keys(updates).length) return Promise.resolve(cart);
    const body = JSON.stringify({
      updates,
      sections: sections,
      sections_url: sectionsUrl || window.location.pathname
    });
    return fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((state) => (state && state.items ? state : cart))
      .catch(() => cart);
  }
};

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'));
  }

  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=main-cart-items`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('cart-items');
        this.innerHTML = sourceQty.innerHTML;
      })
      .catch(e => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents'
      }
    ];
  }

  updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        if (parsedState.errors) return parsedState;
        return CartGiftLink.sync(
          parsedState,
          this.getSectionsToRender().map((section) => section.section),
          window.location.pathname
        );
      })
      .then((parsedState) => {
        const quantityElement = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML =
            this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
        }));
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`)) : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'))
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'))
        }
        publish(PUB_SUB_EVENTS.cartUpdate, {source: 'cart-items'});
      }).catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError = document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

// Al caricamento pagina: allinea/rimuove eventuali omaggi non coerenti gia' presenti
// in carrello (es. prodotto principale rimosso in una sessione precedente) e aggiorna
// drawer, badge e pagina carrello senza ricaricare.
if (!window.__cartGiftLinkInit) {
  window.__cartGiftLinkInit = true;
  const syncGifts = () => {
    fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        if (!Object.keys(CartGiftLink.pendingUpdates(cart)).length) return;
        const targets = [];
        if (document.querySelector('cart-drawer')) {
          targets.push({ section: 'cart-drawer', id: 'CartDrawer', selector: '.drawer__inner' });
        }
        if (document.getElementById('cart-icon-bubble')) {
          targets.push({ section: 'cart-icon-bubble', id: 'cart-icon-bubble', selector: '.shopify-section' });
        }
        const mainCartItems = document.getElementById('main-cart-items');
        if (mainCartItems) {
          targets.push({ section: mainCartItems.dataset.id, id: 'main-cart-items', selector: '.js-contents' });
        }
        const mainCartFooter = document.getElementById('main-cart-footer');
        if (mainCartFooter) {
          targets.push({ section: mainCartFooter.dataset.id, id: 'main-cart-footer', selector: '.js-contents' });
        }
        return CartGiftLink.sync(cart, targets.map((t) => t.section)).then((state) => {
          if (!state || !state.sections) return;
          targets.forEach((target) => {
            const html = state.sections[target.section];
            const container = document.getElementById(target.id);
            if (!html || !container) return;
            const source = new DOMParser().parseFromString(html, 'text/html').querySelector(target.selector);
            const dest = container.querySelector(target.selector) || container;
            if (source) dest.innerHTML = source.innerHTML;
          });
          const isEmpty = state.item_count === 0;
          document.querySelectorAll('cart-drawer, cart-items, cart-drawer-items, #main-cart-footer')
            .forEach((el) => el.classList.toggle('is-empty', isEmpty));
        });
      })
      .catch((e) => console.error('cart gift sync', e));
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncGifts);
  } else {
    syncGifts();
  }
}

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', class CartNote extends HTMLElement {
      constructor() {
        super();

      this.addEventListener('change', debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
      }, ON_CHANGE_DEBOUNCE_TIMER))
      }
  });
};
